import React, { useState, useEffect, useCallback } from 'react';
import { mindApi } from '@/services/api';
import {
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Eye,
  Lightbulb,
  Network,
  User,
  Puzzle,
  Link2,
  Compass,
  X,
  RefreshCw,
  Clock,
} from 'lucide-react';

// ==========================================
// GAP TYPE CONFIG
// ==========================================

const GAP_TYPE_CONFIG = {
  ORPHAN_PERSON: {
    label: 'Orphan Person',
    icon: User,
    color: 'text-rose-400',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/20',
  },
  ISOLATED_CONCEPT: {
    label: 'Isolated Concept',
    icon: Puzzle,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  THIN_EVIDENCE: {
    label: 'Thin Evidence',
    icon: AlertTriangle,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
  SPARSE_NEIGHBORHOOD: {
    label: 'Sparse Neighborhood',
    icon: Network,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  MISSING_EDGE_TYPE: {
    label: 'Missing Edge Type',
    icon: Link2,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/20',
  },
  UNACTIVATED_PRIMITIVE: {
    label: 'Unactivated Primitive',
    icon: Compass,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
  },
};

// ==========================================
// CURIOSITY PACKET CARD
// ==========================================

function CuriosityPacketCard({ packet, onDismiss, dismissing }) {
  const [expanded, setExpanded] = useState(false);
  const config = GAP_TYPE_CONFIG[packet.gap_type] || {
    label: packet.gap_type,
    icon: Lightbulb,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted/10',
    borderColor: 'border-border',
  };
  const Icon = config.icon;
  const isExternal = packet.curiosity_type === 'EXTERNAL';
  const score = typeof packet.priority_score === 'number'
    ? (packet.priority_score * 100).toFixed(0)
    : '?';

  return (
    <div className={`border rounded-lg overflow-hidden ${config.borderColor} transition-all hover:shadow-md`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className={`p-1.5 rounded-md ${config.bgColor}`}>
              <Icon className={`h-4 w-4 ${config.color}`} />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold ${config.color}`}>
                  {config.label}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  isExternal ? 'bg-primary/10 text-primary' : 'bg-orange-500/10 text-orange-400'
                }`}>
                  {isExternal ? 'EXTERNAL' : 'INTERNAL'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] font-mono text-muted-foreground">
              {score}%
            </span>
          </div>
        </div>

        {/* Question */}
        <p className="text-sm text-foreground leading-relaxed mb-3">
          {packet.question}
        </p>

        {/* Expandable details */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          <Eye className="h-3 w-3" />
          Details
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
          {/* Why it matters */}
          {packet.why_it_matters && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Why it matters
              </p>
              <p className="text-xs text-foreground/80 leading-relaxed">
                {packet.why_it_matters}
              </p>
            </div>
          )}

          {/* Expected connections */}
          {packet.expected_connections?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Expected connections
              </p>
              <div className="flex flex-wrap gap-1">
                {packet.expected_connections.map((conn, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 text-[10px] rounded-full bg-accent/50 text-foreground"
                  >
                    {conn}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Suggested approach */}
          {packet.suggested_approach && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Suggested approach
              </p>
              <p className="text-xs text-foreground/80 leading-relaxed">
                {packet.suggested_approach}
              </p>
            </div>
          )}

          {/* What we know */}
          {packet.existing_context && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                What we know
              </p>
              <div className="space-y-1">
                {packet.existing_context.related_concepts?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">Concepts:</span>
                    {packet.existing_context.related_concepts.map((c, i) => (
                      <span key={i} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-500/10 text-purple-300">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
                {packet.existing_context.related_people?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">People:</span>
                    {packet.existing_context.related_people.map((p, i) => (
                      <span key={i} className="px-1.5 py-0.5 text-[10px] rounded bg-rose-500/10 text-rose-300">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {packet.existing_context.active_primitives?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">Primitives:</span>
                    {packet.existing_context.active_primitives.map((p, i) => (
                      <span key={i} className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/10 text-emerald-300">
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {packet.existing_context.relevant_content_snippets?.length > 0 && (
                  <div>
                    <span className="text-[10px] text-muted-foreground">Evidence:</span>
                    {packet.existing_context.relevant_content_snippets.slice(0, 2).map((s, i) => (
                      <p key={i} className="text-[10px] text-foreground/60 italic mt-0.5 line-clamp-2">
                        "{s}"
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reprocessing info (internal only) */}
          {packet.reprocessing && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                Reprocessing target
              </p>
              <p className="text-xs text-foreground/80">
                Service: <span className="font-mono text-orange-400">{packet.reprocessing.service_id}</span>
                {' → '}
                Edge type: <span className="font-mono text-cyan-400">{packet.reprocessing.edge_type}</span>
              </p>
            </div>
          )}

          {/* Metadata footer */}
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60 pt-2 border-t border-border/30">
            <span>{packet.source === 'HEARTBEAT' ? `Heartbeat #${packet.heartbeat_cycle}` : 'On-demand'}</span>
            {packet.created_at && (
              <span className="flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" />
                {new Date(packet.created_at).toLocaleString()}
              </span>
            )}
            <span className="font-mono">{packet.packet_id?.slice(0, 20)}</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-2 border-t border-border/30 bg-card/30 flex items-center justify-end gap-2">
        <button
          onClick={() => onDismiss(packet.packet_id)}
          disabled={dismissing === packet.packet_id}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-accent/50 transition-colors disabled:opacity-50"
        >
          {dismissing === packet.packet_id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ==========================================
// CURIOSITY TAB (MAIN COMPONENT)
// ==========================================

export default function CuriosityTab({ appId }) {
  const [packets, setPackets] = useState([]);
  const [roots, setRoots] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [dismissing, setDismissing] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('PENDING');

  // Fetch root nodes with curiosity enabled
  useEffect(() => {
    if (!appId) return;
    mindApi.getRoots(appId)
      .then(res => {
        const allRoots = Array.isArray(res.data?.data) ? res.data.data : [];
        // Filter to roots that have curiosity in their dreamers
        const curiosityRoots = allRoots.filter(r => {
          if (r.dreamers === 'all') return true;
          if (Array.isArray(r.dreamers)) return r.dreamers.includes('curiosity');
          try { return JSON.parse(r.dreamers || '[]').includes('curiosity'); } catch { return false; }
        });
        setRoots(curiosityRoots);
        if (curiosityRoots.length === 1) setSelectedRoot(curiosityRoots[0].entityId);
      })
      .catch(() => setRoots([]));
  }, [appId]);

  // Fetch packets (requires root)
  const fetchPackets = useCallback(async () => {
    if (!appId || !selectedRoot) return;
    setLoading(true);
    setError(null);

    try {
      const res = await mindApi.getCuriosity(appId, { status: statusFilter, root_id: selectedRoot });
      const data = res.data?.data || res.data;
      setPackets(data?.packets || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load curiosity packets');
    } finally {
      setLoading(false);
    }
  }, [appId, statusFilter, selectedRoot]);

  useEffect(() => {
    if (selectedRoot) fetchPackets();
    else setLoading(false);
  }, [fetchPackets, selectedRoot]);

  // Trigger curiosity evaluation
  const handleTrigger = async () => {
    if (!selectedRoot) return;
    setTriggering(true);
    setError(null);

    try {
      await mindApi.triggerCuriosity(appId, selectedRoot);
      // 202 accepted — packets generate async. Refresh after short delay.
      setTimeout(() => fetchPackets(), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Curiosity evaluation failed');
    } finally {
      setTriggering(false);
    }
  };

  // Dismiss packet
  const handleDismiss = async (packetId) => {
    setDismissing(packetId);
    try {
      await mindApi.dismissCuriosity(appId, packetId);
      setPackets((prev) => prev.filter((p) => p.packet_id !== packetId));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to dismiss');
    } finally {
      setDismissing(null);
    }
  };

  // Group packets by curiosity type
  const externalPackets = packets.filter((p) => p.curiosity_type === 'EXTERNAL');
  const internalPackets = packets.filter((p) => p.curiosity_type === 'INTERNAL');

  return (
    <div className="space-y-6">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Curiosity Engine
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Knowledge gaps detected in the cognitive graph, prioritized by app context.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Root selector */}
          <select
            value={selectedRoot || ''}
            onChange={(e) => setSelectedRoot(e.target.value || null)}
            className="text-xs bg-background border rounded-md px-2 py-1.5 text-foreground"
          >
            <option value="">Select root node...</option>
            {roots.map(r => (
              <option key={r.entityId} value={r.entityId}>
                {r.name || r.entityId}
              </option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs bg-background border rounded-md px-2 py-1.5 text-foreground"
          >
            <option value="PENDING">Pending</option>
            <option value="DELIVERED">Delivered</option>
            <option value="ANSWERED">Answered</option>
            <option value="DISMISSED">Dismissed</option>
          </select>

          {/* Refresh */}
          <button
            onClick={fetchPackets}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Trigger button */}
          <button
            onClick={handleTrigger}
            disabled={triggering || loading || !selectedRoot}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {triggering ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Explore Curiosity
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg">
          {error}
        </div>
      )}

      {/* No root selected */}
      {!selectedRoot && !loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Compass className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            {roots.length === 0
              ? 'No root nodes with curiosity enabled. Create a root node with curiosity in its dreamers.'
              : 'Select a root node to view its curiosity packets.'}
          </p>
        </div>
      )}

      {/* Loading state */}
      {loading && selectedRoot && packets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
          <p className="text-sm text-muted-foreground">Loading curiosity packets...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && packets.length === 0 && !error && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Sparkles className="h-12 w-12 mb-4 opacity-30" />
          <p className="text-sm font-medium mb-1">No curiosity packets yet</p>
          <p className="text-xs text-center max-w-sm">
            Click "Explore Curiosity" to analyze the cognitive graph for knowledge gaps.
            The system will identify orphan nodes, thin evidence, sparse neighborhoods,
            and missing connections — then generate contextual questions to fill those gaps.
          </p>
        </div>
      )}

      {/* Triggering state overlay */}
      {triggering && (
        <div className="flex flex-col items-center justify-center py-8 gap-3 border rounded-lg bg-card/50">
          <div className="relative">
            <Sparkles className="h-8 w-8 text-primary animate-pulse" />
          </div>
          <p className="text-sm text-muted-foreground">Analyzing graph for knowledge gaps...</p>
          <div className="flex gap-2 text-[10px] text-muted-foreground">
            <span>Gap detection</span>
            <span>→</span>
            <span>Prioritization</span>
            <span>→</span>
            <span>Question generation</span>
          </div>
        </div>
      )}

      {/* Results */}
      {!loading && packets.length > 0 && (
        <div className="space-y-6">
          {/* Summary bar */}
          <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
            <span>{packets.length} packet{packets.length !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span>{externalPackets.length} external</span>
            <span>·</span>
            <span>{internalPackets.length} internal</span>
          </div>

          {/* External packets */}
          {externalPackets.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                External — Needs new data
              </h3>
              {externalPackets.map((packet) => (
                <CuriosityPacketCard
                  key={packet.packet_id}
                  packet={packet}
                  onDismiss={handleDismiss}
                  dismissing={dismissing}
                />
              ))}
            </div>
          )}

          {/* Internal packets */}
          {internalPackets.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Internal — Reprocessing available
              </h3>
              {internalPackets.map((packet) => (
                <CuriosityPacketCard
                  key={packet.packet_id}
                  packet={packet}
                  onDismiss={handleDismiss}
                  dismissing={dismissing}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
